<template>
  <div class="common-layout">
    <el-container class="app-shell">
      <el-header class="app-header">
        <el-button v-if="isMobile" class="menu-toggle" link @click="drawerOpen = true">
          <el-icon size="22" color="#fff"><Menu /></el-icon>
        </el-button>
        <HeaderView />
      </el-header>
      <el-container class="app-body">
        <el-aside v-if="!isMobile" class="app-aside" width="200px">
          <NavMenu />
        </el-aside>
        <el-drawer
          v-else
          v-model="drawerOpen"
          direction="ltr"
          size="220px"
          :with-header="false"
          class="nav-drawer"
        >
          <NavMenu @navigate="drawerOpen = false" />
        </el-drawer>
        <el-main class="app-main">
          <!--
            Gate the routed view on `ready`. When the page lands with a
            `?ticket=...` in the URL we need to POST it to /user/validate
            and store the JWT BEFORE any protected view fires its own
            API calls; otherwise those calls race the validate, come
            back 401, and bounce us straight to CAS again — producing
            the redirect-loop the user sees. `ready` flips to true after
            validate resolves (or immediately when there's no ticket).
          -->
          <router-view v-if="ready"></router-view>
          <div v-else class="auth-loading">登录中…</div>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { RouterView } from "vue-router";
import { ElMessage } from "element-plus";
import HeaderView from "./views/HeaderView.vue";
import NavMenu from "./components/NavMenu.vue";
import { Menu } from "@element-plus/icons-vue";
import { setAuthToken, getAuthToken, getQuery } from "./api/util";
import { validateUser } from "./api/http";
import { loadMe } from "./api/currentUser";
import { useBreakpoint } from "./composables/useBreakpoint";

const { isMobile } = useBreakpoint();
const drawerOpen = ref(false);

// When there's a `?ticket=` we must NOT render the protected views until
// /user/validate completes — otherwise child views mount, fire API calls
// with no JWT, get 401, and the axios interceptor bounces back to CAS,
// which immediately re-issues a ticket. That's the "登录后无限 302"
// loop the user reported.
const query = getQuery();
const hasTicket = !!(query["ticket"] && query["ticket"].length > 0);
const ready = ref(!hasTicket);

watch(isMobile, (m) => { if (!m) drawerOpen.value = false; });

onMounted(async () => {
  if (!hasTicket) {
    // Already authenticated on this load — fetch identity + permissions so the
    // nav (admin link) and new-account controls render correctly.
    if (getAuthToken()) void loadMe();
    return;
  }
  try {
    const data = await validateUser(query["ticket"]);
    if (data.successful) {
      setAuthToken(data.result);
      // Reload onto a clean URL so the ticket query-string is gone and
      // the router-guard path takes over for subsequent navigation.
      window.location.href = import.meta.env.VITE_APP_URL;
      // Don't flip `ready` — the browser will navigate away momentarily.
      return;
    }
    ElMessage.error(data.message ?? "登录验证失败");
    ready.value = true;
  } catch (e) {
    ElMessage.error((e as Error)?.message ?? "登录验证失败");
    ready.value = true;
  }
});

void Menu;
</script>

<style>
.app-shell {
  height: 100vh;
}

.app-header {
  background: #545c64;
  border-bottom: 1px solid #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
}

.menu-toggle {
  padding: 0 4px;
}

.app-body {
  flex: 1;
  min-height: 0;
}

.app-aside {
  background: #545c64;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.app-menu {
  border-right: none;
  min-height: 100%;
}

.app-main {
  height: 100%;
  overflow: auto;
}

.auth-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 14px;
}

.nav-drawer .el-drawer__body {
  padding: 0;
  background: #545c64;
}

@media (max-width: 768px) {
  .app-header {
    padding: 0 8px;
  }
  .app-main {
    padding: 12px;
  }
  .el-dialog {
    width: min(500px, 92vw) !important;
  }
}
</style>
